import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, Eye, Target, Calendar, Package, Users, MapPin, Award, Leaf, Snowflake, CheckCircle } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

const CERTIFICATIONS = [
  { name: "FSSAI License", desc: "Food Safety and Standards Authority of India", icon: <ShieldCheck size={32} /> },
  { name: "ISO 22000", desc: "Food Safety Management System Certification", icon: <Award size={32} /> },
  { name: "HACCP Compliant", desc: "Hazard Analysis Critical Control Points", icon: <CheckCircle size={32} /> },
  { name: "100% Natural", desc: "No Preservatives, No Artificial Colors", icon: <Leaf size={32} /> },
  { name: "Cold Chain Certified", desc: "Unbroken Cold Chain from Farm to Delivery", icon: <Snowflake size={32} /> },
];

const STATS = [
  { label: "Years of Excellence", value: "10+", icon: <Calendar size={28} /> },
  { label: "Products Range", value: "50+", icon: <Package size={28} /> },
  { label: "Satisfied Clients", value: "200+", icon: <Users size={28} /> },
  { label: "Cities Served", value: "25+", icon: <MapPin size={28} /> },
];

export default function About() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);

  return (
    <div className="pt-20" data-testid="about-page">
      {/* Hero + Company Story — shared parallax background */}
      <div ref={heroRef} className="relative overflow-hidden">
        <motion.img
          src="https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1600&q=80"
          alt=""
          aria-hidden="true"
          style={{ y: imageY }}
          className="absolute inset-0 w-full h-[150%] object-cover opacity-[0.13] scale-110"
        />

      {/* Hero */}
      <section className="py-16 md:py-24 bg-[#f8f8f8]/80">
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp}>
              <Badge className="bg-green-100 text-[#2B7A1E] hover:bg-green-100 mb-4 px-4 py-1.5 text-xs font-semibold tracking-wide rounded-full border-0">
                About Us
              </Badge>
            </motion.div>
            <motion.h1 variants={fadeUp} className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#333] leading-tight tracking-tight mb-6" style={{ fontFamily: 'Montserrat' }}>
              Our Story
            </motion.h1>
            <motion.div variants={fadeUp} className="w-16 h-1 bg-[#2B7A1E] rounded-full" />
          </motion.div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-16 md:py-24 bg-white/80">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
              <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-bold text-[#333] tracking-tight mb-6" style={{ fontFamily: 'Montserrat' }}>
                About Shri Ram Frozen Foods
              </motion.h2>
              <motion.p variants={fadeUp} className="text-gray-600 leading-relaxed mb-4">
                Shri Ram Frozen Foods is a Pune-based supplier of high-quality frozen fruits, pulps, purees, berries, and vegetables. With over a decade of experience in the frozen food industry, we have built a reputation for delivering premium-grade products to food businesses across India.
              </motion.p>
              <motion.p variants={fadeUp} className="text-gray-600 leading-relaxed mb-4">
                We work closely with trusted farms and state-of-the-art processing units to ensure that every product meets strict standards of purity, hygiene, and consistency. Using advanced IQF (Individual Quick Freezing) and cold-chain technology, we bring food businesses a reliable supply of natural, preservative-free ingredients throughout the year.
              </motion.p>
              <motion.p variants={fadeUp} className="text-gray-600 leading-relaxed mb-6">
                Our commitment to quality, competitive pricing, and customer-centric approach has made us the preferred partner for ice cream manufacturers, beverage companies, bakeries, HORECA establishments, and FMCG brands.
              </motion.p>
              <motion.div variants={fadeUp}>
                <Link to="/products">
                  <Button className="rounded-full bg-[#2B7A1E] hover:bg-[#236618] text-white font-semibold px-8 shadow-lg shadow-green-900/15" data-testid="about-explore-btn">
                    Explore Our Products
                  </Button>
                </Link>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="relative"
            >
              <div className="rounded-3xl overflow-hidden shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1438907046657-4ae137eb8c5e?w=800&q=80"
                  alt="Frozen food processing"
                  className="w-full h-[400px] object-cover"
                  loading="lazy"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-[#2B7A1E] text-white p-6 rounded-2xl shadow-xl max-w-[200px]" data-testid="about-experience-badge">
                <p className="text-3xl font-bold" style={{ fontFamily: 'Montserrat' }}>10+</p>
                <p className="text-sm text-white/80 mt-1">Years of trusted supply across India</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      </div>{/* end parallax wrapper */}

      {/* Vision & Mission */}
      <section className="py-16 md:py-24 bg-[#f8f8f8]" data-testid="vision-mission-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-bold text-[#333] tracking-tight" style={{ fontFamily: 'Montserrat' }}>
              Our Vision & Mission
            </motion.h2>
            <motion.div variants={fadeUp} className="w-16 h-1 bg-[#2B7A1E] mt-4 mx-auto rounded-full" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 0 }}
              className="bg-white p-8 md:p-10 rounded-3xl border border-gray-100 shadow-sm"
            >
              <div className="w-14 h-14 rounded-2xl bg-green-50 flex items-center justify-center text-[#2B7A1E] mb-6">
                <Eye size={28} />
              </div>
              <h3 className="text-2xl font-bold text-[#333] mb-4" style={{ fontFamily: 'Montserrat' }}>Our Vision</h3>
              <ul className="space-y-3">
                {[
                  "Deliver premium frozen ingredients nationwide",
                  "Maintain quality, purity & consistency in every batch",
                  "Build long-term partnerships with food businesses",
                  "Lead the market with innovation & hygiene standards"
                ].map((item) => (
                  <li key={item} className="flex gap-3 text-gray-600">
                    <CheckCircle size={18} className="text-[#2B7A1E] mt-0.5 flex-shrink-0" />
                    <span className="text-sm leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
              className="bg-white p-8 md:p-10 rounded-3xl border border-gray-100 shadow-sm"
            >
              <div className="w-14 h-14 rounded-2xl bg-orange-50 flex items-center justify-center text-[#D94F04] mb-6">
                <Target size={28} />
              </div>
              <h3 className="text-2xl font-bold text-[#333] mb-4" style={{ fontFamily: 'Montserrat' }}>Our Mission</h3>
              <ul className="space-y-3">
                {[
                  "Source pure and natural fruits directly from farms",
                  "Use advanced IQF & cold-chain processing technology",
                  "Provide reliable year-round supply to food industries",
                  "Offer consistent quality at competitive pricing"
                ].map((item) => (
                  <li key={item} className="flex gap-3 text-gray-600">
                    <CheckCircle size={18} className="text-[#D94F04] mt-0.5 flex-shrink-0" />
                    <span className="text-sm leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-[#002f47] relative overflow-hidden" data-testid="stats-section">
        {/* Background cold image */}
        <img src="/cold1.webp" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-[0.12]" />
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#002f47] via-transparent to-[#002f47]" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            variants={{ visible: { transition: { staggerChildren: 0.15 } } }}
            className="grid grid-cols-2 md:grid-cols-4 gap-5"
          >
            {STATS.map((stat) => (
              <motion.div
                key={stat.label}
                variants={{ hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } } }}
                className="relative bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8 text-center backdrop-blur-sm hover:bg-white/10 transition-colors duration-300 group"
              >
                {/* Green top accent bar */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-10 h-[3px] bg-[#2B7A1E] rounded-full" />
                <div className="w-14 h-14 rounded-xl bg-white/10 flex items-center justify-center text-green-300 mx-auto mb-4 group-hover:scale-110 group-hover:bg-white/15 transition-all duration-300">
                  {stat.icon}
                </div>
                <p className="text-4xl md:text-5xl font-bold text-white mb-1 tracking-tight" style={{ fontFamily: 'Montserrat' }}>{stat.value}</p>
                <p className="text-xs md:text-sm text-white/55 tracking-wide mt-1 uppercase" style={{ fontFamily: 'Montserrat' }}>{stat.label}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-16 md:py-24 bg-white" data-testid="certifications-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="text-center mb-14">
              <span className="text-sm font-semibold text-[#2B7A1E] uppercase tracking-wider" style={{ fontFamily: 'Montserrat' }}>Quality Assurance</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#333] mt-2 tracking-tight" style={{ fontFamily: 'Montserrat' }}>
                Our Certifications
              </h2>
              <div className="w-16 h-1 bg-[#2B7A1E] mt-4 mx-auto rounded-full" />
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {CERTIFICATIONS.map((cert) => (
                <motion.div key={cert.name} variants={fadeUp}>
                  <div className="p-6 rounded-2xl bg-[#f8f8f8] border border-gray-100 hover:border-green-200 transition-colors duration-300 text-center" data-testid={`cert-${cert.name.toLowerCase().replace(/\s/g, '-')}`}>
                    <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center text-[#2B7A1E] mx-auto mb-4">
                      {cert.icon}
                    </div>
                    <h3 className="text-lg font-bold text-[#333]" style={{ fontFamily: 'Montserrat' }}>{cert.name}</h3>
                    <p className="text-sm text-gray-500 mt-1">{cert.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#2B7A1E] relative overflow-hidden">
        <img
          src="/cold5.webp"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-4" style={{ fontFamily: 'Montserrat' }}>
            Partner With Us
          </h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto">
            Join 200+ food businesses that trust Shri Ram Frozen Foods for their frozen ingredient needs.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/contact">
              <Button className="rounded-full bg-white text-[#2B7A1E] font-bold px-8 h-12 hover:bg-gray-100 shadow-lg" data-testid="about-cta-contact">
                Get in Touch
              </Button>
            </Link>
            <Link to="/products">
              <Button variant="outline" className="rounded-full border-2 border-white text-white font-semibold px-8 h-12 hover:bg-white/10" data-testid="about-cta-products">
                View Products
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
